import { useEffect, useRef, useState } from "react";
import api, { API, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Building2, Upload, Save, ImageOff, ShieldCheck } from "lucide-react";

const EMPTY = {
  raisonSociale: "", siret: "", adresse: "", secteur: "", telephone: "", email: "", siteWeb: "",
  responsableHSE: { nom: "", fonction: "Responsable HSE", tel: "", email: "" },
  responsableTechnique: { nom: "", fonction: "Responsable technique / maintenance", tel: "", email: "" },
  consignesGenerales: { epi: "", circulation: "", interdictions: "", environnement: "", specifique: "" },
  urgence: { numeroUrgence: "", secoursExternes: "18 / 112 · 15 (SAMU)", pointRassemblement: "" },
};

export default function Organisation() {
  const { user } = useAuth();
  const canEdit = user?.role === "ADMINISTRATEUR";
  const [org, setOrg] = useState(EMPTY);
  const [logoUrl, setLogoUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => {
    api.get("/organisation").then(({ data }) => {
      setOrg({ ...EMPTY, ...data,
        responsableHSE: { ...EMPTY.responsableHSE, ...(data.responsableHSE || {}) },
        responsableTechnique: { ...EMPTY.responsableTechnique, ...(data.responsableTechnique || {}) },
        consignesGenerales: { ...EMPTY.consignesGenerales, ...(data.consignesGenerales || {}) },
        urgence: { ...EMPTY.urgence, ...(data.urgence || {}) } });
      setLogoUrl(data.logoFileId ? `${API}/files/${data.logoFileId}?t=${Date.now()}` : null);
    });
  };
  useEffect(load, []);

  const set = (k, v) => setOrg((o) => ({ ...o, [k]: v }));
  const setSub = (grp, k, v) => setOrg((o) => ({ ...o, [grp]: { ...o[grp], [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/organisation", org);
      toast.success("Profil de l'organisation enregistré. Il sera repris dans vos Plans de Prévention.");
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/organisation/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo mis à jour");
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div data-testid="organisation-page">
      <div className="page-head">
        <div><div className="crumb">Référentiel · Organisation</div><h1>Profil de l'organisation</h1></div>
        {canEdit && <button className="btn btn-p" onClick={save} disabled={busy} data-testid="org-save"><Save size={15} />{busy ? "Enregistrement…" : "Enregistrer"}</button>}
      </div>
      <div className="notice" data-testid="org-privacy"><ShieldCheck size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
        Ces informations sont privées, réservées à votre organisation, et reprises automatiquement dans vos Plans de Prévention. {!canEdit && "(Lecture seule — réservé à l'administrateur.)"}
      </div>

      {/* Logo + identité */}
      <div className="card" style={{ borderLeft: "4px solid var(--sarcelle)" }}>
        <h3 style={{ fontSize: "1rem", display: "flex", alignItems: "center", gap: 8 }}><Building2 size={17} color="var(--sarcelle)" />Identité</h3>
        <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginTop: 10 }}>
          <div style={{ textAlign: "center" }}>
            <div data-testid="org-logo-preview" style={{ width: 150, height: 84, border: "1px dashed var(--border)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background: "var(--surface-2)" }}>
              {logoUrl ? <img src={logoUrl} alt="Logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                : <span style={{ color: "var(--text-muted)", fontSize: ".75rem", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}><ImageOff size={20} />Aucun logo</span>}
            </div>
            {canEdit && <>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onLogo} style={{ display: "none" }} data-testid="org-logo-input" />
              <button className="btn btn-g btn-sm" style={{ marginTop: 8 }} onClick={() => fileRef.current?.click()} data-testid="org-logo-btn"><Upload size={13} />Logo</button>
            </>}
          </div>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div className="row2">
              <div><label className="f">Raison sociale</label><input value={org.raisonSociale} disabled={!canEdit} onChange={(e) => set("raisonSociale", e.target.value)} data-testid="org-raisonSociale" placeholder="Ex : MACIF" /></div>
              <div><label className="f">SIRET</label><input value={org.siret} disabled={!canEdit} onChange={(e) => set("siret", e.target.value)} data-testid="org-siret" /></div>
            </div>
            <label className="f">Adresse</label>
            <input value={org.adresse} disabled={!canEdit} onChange={(e) => set("adresse", e.target.value)} data-testid="org-adresse" />
            <div className="row3">
              <div><label className="f">Secteur d'activité</label><input value={org.secteur} disabled={!canEdit} onChange={(e) => set("secteur", e.target.value)} data-testid="org-secteur" /></div>
              <div><label className="f">Téléphone</label><input value={org.telephone} disabled={!canEdit} onChange={(e) => set("telephone", e.target.value)} data-testid="org-telephone" /></div>
              <div><label className="f">Email général</label><input value={org.email} disabled={!canEdit} onChange={(e) => set("email", e.target.value)} data-testid="org-email" /></div>
            </div>
          </div>
        </div>
      </div>

      {/* Contacts clés */}
      <div className="card">
        <h3 style={{ fontSize: "1rem" }}>Contacts clés</h3>
        {[["responsableHSE", "Responsable HSE / Prévention"], ["responsableTechnique", "Responsable technique / maintenance"]].map(([grp, lbl]) => (
          <div key={grp} style={{ marginTop: 10 }}>
            <label className="f" style={{ fontWeight: 600 }}>{lbl}</label>
            <div className="row3">
              <input placeholder="Nom" value={org[grp].nom} disabled={!canEdit} onChange={(e) => setSub(grp, "nom", e.target.value)} data-testid={`org-${grp}-nom`} />
              <input placeholder="Téléphone" value={org[grp].tel} disabled={!canEdit} onChange={(e) => setSub(grp, "tel", e.target.value)} data-testid={`org-${grp}-tel`} />
              <input placeholder="Email" value={org[grp].email} disabled={!canEdit} onChange={(e) => setSub(grp, "email", e.target.value)} data-testid={`org-${grp}-email`} />
            </div>
          </div>
        ))}
      </div>

      {/* Consignes générales */}
      <div className="card">
        <h3 style={{ fontSize: "1rem" }}>Consignes générales de sécurité</h3>
        <p style={{ fontSize: ".78rem", color: "var(--text-muted)" }}>Reprises dans le §5 du Plan de Prévention.</p>
        <div className="row2">
          <div><label className="f">EPI obligatoires</label><input value={org.consignesGenerales.epi} disabled={!canEdit} onChange={(e) => setSub("consignesGenerales", "epi", e.target.value)} data-testid="org-cg-epi" /></div>
          <div><label className="f">Circulation</label><input value={org.consignesGenerales.circulation} disabled={!canEdit} onChange={(e) => setSub("consignesGenerales", "circulation", e.target.value)} data-testid="org-cg-circulation" /></div>
        </div>
        <div className="row2">
          <div><label className="f">Interdictions</label><input value={org.consignesGenerales.interdictions} disabled={!canEdit} onChange={(e) => setSub("consignesGenerales", "interdictions", e.target.value)} data-testid="org-cg-interdictions" /></div>
          <div><label className="f">Environnement</label><input value={org.consignesGenerales.environnement} disabled={!canEdit} onChange={(e) => setSub("consignesGenerales", "environnement", e.target.value)} data-testid="org-cg-environnement" /></div>
        </div>
        <label className="f">Consigne spécifique</label>
        <input value={org.consignesGenerales.specifique} disabled={!canEdit} onChange={(e) => setSub("consignesGenerales", "specifique", e.target.value)} data-testid="org-cg-specifique" />
      </div>

      {/* Urgences */}
      <div className="card">
        <h3 style={{ fontSize: "1rem" }}>Organisation des secours & urgences</h3>
        <p style={{ fontSize: ".78rem", color: "var(--text-muted)" }}>Reprises dans le §8 du Plan de Prévention.</p>
        <div className="row3">
          <div><label className="f">Numéro d'urgence interne</label><input value={org.urgence.numeroUrgence} disabled={!canEdit} onChange={(e) => setSub("urgence", "numeroUrgence", e.target.value)} data-testid="org-urg-numero" /></div>
          <div><label className="f">Secours externes</label><input value={org.urgence.secoursExternes} disabled={!canEdit} onChange={(e) => setSub("urgence", "secoursExternes", e.target.value)} data-testid="org-urg-externes" /></div>
          <div><label className="f">Point de rassemblement</label><input value={org.urgence.pointRassemblement} disabled={!canEdit} onChange={(e) => setSub("urgence", "pointRassemblement", e.target.value)} data-testid="org-urg-point" /></div>
        </div>
      </div>
    </div>
  );
}
